import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin, Clock, ExternalLink, Facebook, Instagram } from "lucide-react";
import { FACEBOOK_URL, INSTAGRAM_URL } from "@/data/content";
import { useSchedule } from "@/hooks/useSchedule";

const isoOf = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

// Prefer Apple Maps on Apple devices (iOS/iPadOS/macOS), Google Maps everywhere else.
const directionsUrl = (address) => {
  const q = encodeURIComponent(address);
  const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
  const isApple =
    /iPad|iPhone|iPod/.test(ua) ||
    /Macintosh|Mac OS X/.test(ua) ||
    (typeof navigator !== "undefined" &&
      navigator.platform === "MacIntel" &&
      navigator.maxTouchPoints > 1);
  return isApple
    ? `https://maps.apple.com/?q=${q}`
    : `https://maps.google.com/?q=${q}`;
};

export const Locations = () => {
  const locations = useSchedule();
  const [weekOffset, setWeekOffset] = useState(0);

  const now = new Date();
  const todayIso = isoOf(now);
  const nowMin = now.getHours() * 60 + now.getMinutes();
  const toMin = (t) => {
    const [h, m] = t.split(":").map(Number);
    return h * 60 + m;
  };

  // Monday → Sunday range for the selected week
  const mondayOffset = (now.getDay() + 6) % 7; // 0 = Mon ... 6 = Sun
  const monday = new Date(now);
  monday.setHours(0, 0, 0, 0);
  monday.setDate(now.getDate() - mondayOffset + weekOffset * 7);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  const weekStartIso = isoOf(monday);
  const weekEndIso = isoOf(sunday);

  const weekStops = locations.filter((l) => l.iso >= weekStartIso && l.iso <= weekEndIso);

  const tabClass = (active) =>
    `px-5 py-2.5 rounded-full text-sm font-bold transition-colors ${
      active ? "bg-[#111111] text-[#f7f0eb] shadow" : "text-[#3d3d3d] hover:text-[#111111]"
    }`;

  return (
    <section id="locations" data-testid="locations-section" className="py-20 md:py-28 px-5 md:px-10 bg-[#ff648b]">
      <div className="max-w-6xl mx-auto">
        <div className="text-center max-w-2xl mx-auto">
          <h2 className="font-display font-bold text-4xl sm:text-5xl text-[#111111] tracking-tight flex items-center justify-center gap-3">
            <MapPin className="h-9 w-9 sm:h-12 sm:w-12 text-[#111111] shrink-0" strokeWidth={2.5} />
            Where to find us
          </h2>
          <p className="text-lg text-[#111111]/80 mt-4">
            We post fresh stops every week. Check back here for upcoming locations
            and follow us on social media for surprise pop-ups.
          </p>

          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <div className="inline-flex rounded-full bg-white/70 border-2 border-[#111111]/15 p-1 shadow-md">
              <button
                data-testid="week-this-btn"
                onClick={() => setWeekOffset(0)}
                className={tabClass(weekOffset === 0)}
              >
                This Week
              </button>
              <button
                data-testid="week-next-btn"
                onClick={() => setWeekOffset(1)}
                className={tabClass(weekOffset === 1)}
              >
                Next Week
              </button>
            </div>

            <div
              data-testid="locations-social-pill"
              className="inline-flex items-stretch rounded-full overflow-hidden bg-white/70 border-2 border-[#111111]/15 shadow-md"
            >
              <a
                data-testid="locations-facebook-link"
                href={FACEBOOK_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Bobo's on Facebook"
                className="flex items-center justify-center px-5 py-2.5 text-[#111111] hover:bg-[#1877F2] hover:text-white transition-colors"
              >
                <Facebook className="h-5 w-5" />
              </a>
              <div className="w-px self-stretch bg-[#111111]/15" />
              <a
                data-testid="locations-instagram-link"
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Bobo's on Instagram"
                className="flex items-center justify-center px-5 py-2.5 text-[#111111] hover:text-white hover:bg-[linear-gradient(45deg,#f09433,#e6683c,#dc2743,#cc2366,#bc1888)] transition-colors"
              >
                <Instagram className="h-5 w-5" />
              </a>
            </div>
          </div>
        </div>

        <div className="relative overflow-hidden mt-8 pt-4">
          <AnimatePresence mode="wait">
            <motion.div
              key={weekOffset}
              initial={{ opacity: 0, x: 70 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -70 }}
              transition={{ duration: 0.35, ease: "easeInOut" }}
              className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-4xl mx-auto"
            >
              {weekStops.length === 0 ? (
                <div data-testid="locations-empty" className="col-span-full text-center bg-white/60 rounded-3xl py-12 px-6">
                  <p className="font-display font-bold text-xl text-[#111111]">
                    No stops posted for this week yet.
                  </p>
                  <p className="text-[#111111]/70 mt-2">
                    Check back soon — new locations drop every single week!
                  </p>
                </div>
              ) : (
                weekStops.map((loc, i) => {
                  const isPublic = !!loc.time;
                  const isToday = loc.iso === todayIso;
                  const highlight = isToday && isPublic;
                  const isNow =
                    highlight &&
                    loc.start &&
                    loc.end &&
                    nowMin >= toMin(loc.start) &&
                    nowMin <= toMin(loc.end);
                  return (
                    <div
                      key={`${loc.iso}-${i}`}
                      data-testid={`location-card-${i}`}
                      className={`relative bg-white rounded-3xl p-6 border-2 ${
                        highlight
                          ? "border-[#7bd389] ring-4 ring-[#7bd389]/30 shadow-[0_14px_44px_rgba(123,211,137,0.32)]"
                          : "border-[#ff648b]/30 shadow-[0_8px_30px_rgba(244,169,184,0.15)]"
                      }`}
                    >
                      {highlight && (
                        <span
                          data-testid={`location-badge-${i}`}
                          className={`absolute -top-3 right-5 inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide shadow-md ${
                            isNow ? "bg-[#7bd389] text-[#1f3d22]" : "bg-[#111111] text-[#f7f0eb]"
                          }`}
                        >
                          {isNow && (
                            <span className="relative flex h-2 w-2">
                              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#1f3d22]/60 opacity-75" />
                              <span className="relative inline-flex rounded-full h-2 w-2 bg-[#1f3d22]" />
                            </span>
                          )}
                          {isNow ? "Now Serving" : "Today's Stop"}
                        </span>
                      )}
                      <span className="font-display font-bold text-2xl text-[#ed4a76]">{loc.day}</span>
                      {loc.date && <span className="block text-sm font-semibold text-[#3d3d3d]/70 mt-0.5">{loc.date}</span>}
                      <h3 className="font-display font-semibold text-lg text-[#111111] mt-2">{loc.place}</h3>
                      {loc.note && (
                        <p
                          data-testid={`location-note-${i}`}
                          className="text-sm text-[#3d3d3d] mt-2 italic"
                        >
                          {loc.note}
                        </p>
                      )}
                      {isPublic ? (
                        <>
                          <p className="flex items-center gap-1.5 text-sm text-[#3d3d3d] mt-3">
                            <Clock className="h-4 w-4 text-[#ff648b]" /> {loc.time}
                          </p>
                          {loc.address && (
                            <p className="flex items-start gap-1.5 text-sm text-[#3d3d3d] mt-1.5">
                              <MapPin className="h-4 w-4 text-[#ff648b] shrink-0 mt-0.5" /> {loc.address}
                            </p>
                          )}
                          <div className="mt-4 flex flex-col items-start gap-2">
                            {loc.address && (
                              <a
                                href={directionsUrl(loc.address)}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid={`location-map-${i}`}
                                className="inline-flex items-center gap-1 text-sm font-semibold text-[#111111] hover:text-[#ed4a76]"
                              >
                                Get directions <ExternalLink className="h-3.5 w-3.5" />
                              </a>
                            )}
                            {loc.url && (
                              <a
                                href={loc.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid={`location-website-${i}`}
                                className="inline-flex items-center gap-1 text-sm font-semibold text-[#111111] hover:text-[#ed4a76]"
                              >
                                Location Website <ExternalLink className="h-3.5 w-3.5" />
                              </a>
                            )}
                          </div>
                        </>
                      ) : (
                        !loc.note && (
                          <p className="text-sm text-[#3d3d3d] mt-3 italic">Not open to the public this day.</p>
                        )
                      )}
                    </div>
                  );
                })
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};
